import type { ColumnDef } from '@tanstack/react-table';
import type { LocaleLocaleAdminReadModel } from '@r/api/generated/client.schemas.ts';
import { useTranslation } from 'react-i18next';
import { AdminDataTableRowActions } from '../datatable/admin-data-table-row-actions.tsx';

type Locale = LocaleLocaleAdminReadModel;

interface Option {
  label: string;
  value: string;
}

interface DataTableFilterField<TData> {
  label: string;
  value: keyof TData | string;
  placeholder?: string;
  options?: Option[];
}

export const useFilterFields = (): DataTableFilterField<Locale>[] => {
  const { t } = useTranslation();
  return [
    {
      label: t('label.search'),
      value: 'search',
      placeholder: t('placeholder.search.translations')
    }
  ];
};

export const useAdminTranslationColumns = (
  isUserSuperAdmin: boolean,
  onEditLocale: (value: Locale) => void,
  onDeleteLocale: (value: Locale) => void
): ColumnDef<Locale>[] => {
  const { t } = useTranslation();

  const columns: ColumnDef<Locale>[] = [
    {
      accessorKey: 'label' as keyof Locale,
      id: 'label',
      header: t('label.translation.label')
    },
    {
      id: 'systemTranslation',
      header: t('label.translation.systemtranslation'),
      cell: ({ row }) => {
        const locale = row.original;

        const systemContent = locale.isCustom ? locale.defaultContent : locale.content;
        return <div title={systemContent ?? '-'}>{systemContent || '-'}</div>;
      }
    },
    {
      id: 'search',
      header: 'Search Filter (Hidden)',
      enableHiding: true,
      cell: () => null
    }
  ];

  const finalColumns = isUserSuperAdmin
    ? columns
    : columns.filter((col) => col.id !== 'systemTranslation');

  finalColumns.push({
    id: 'actions',
    cell: ({ row }) => (
      <AdminDataTableRowActions
        row={row}
        onEditLocale={onEditLocale}
        onDeleteLocale={onDeleteLocale}
      />
    )
  });

  return finalColumns;
};
